import React from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';

const TIERS = [
  {
    name: 'Basic Membership',
    price: 'R100',
    color: '#1b7a30',
    purpose: 'Affordable access to paralegal knowledge and workplace guidance.',
    benefits: [
      'Unlimited access to educational materials and paralegal guidance on workplace matters',
      'Assistance drafting a written case statement or summary if workplace issues arise',
      'Continuous advisory communication via WhatsApp, email, or in-person consultations',
      'Access to updates on labour rights and workplace protection resources',
      'Case follow-up support until the matter is concluded (advisory only)',
    ],
    limitations: [
      'No document drafting, form submission, or accompaniment benefits',
      'May upgrade or pay once-off for higher-level services when needed',
    ],
    upgrades: [
      'Document drafting / forms filing: R100 per document',
      'Accompaniment to meetings / CCMA: R150 per trip',
    ],
  },
  {
    name: 'Standard Membership',
    price: 'R150',
    color: '#1a5fb4',
    purpose: 'Extended membership for active workplace cases or ongoing disputes.',
    benefits: [
      'Everything in Basic Membership',
      'Document drafting and forms preparation (letters, grievances, CCMA referral forms, UIF or provident fund claim forms, etc.)',
      'Assistance submitting documents to relevant bodies (e.g. CCMA, Bargaining Council, HR departments)',
      'Guided administrative support until confirmation of case registration or acceptance',
      'Follow-up communication and case monitoring until closure',
    ],
    limitations: [
      'Does not include physical accompaniment to hearings or meetings',
    ],
    upgrades: [
      'Accompaniment to meetings / CCMA as a supportive friend: R150 per trip',
    ],
  },
  {
    name: 'VIP Membership',
    price: 'R380',
    color: 'var(--color-gold)',
    purpose: 'Full paralegal support and personal accompaniment for confidence and solidarity.',
    benefits: [
      'Everything in Basic and Standard Membership',
      'Accompaniment to meetings, CCMA sessions, or workplace hearings as a supportive friend, not as a legal representative',
      'Emotional and strategic support during case proceedings',
      'Pre-hearing preparation coaching to understand CCMA or HR processes',
      'Ongoing advisory communication and check-ins until the case has concluded',
    ],
    limitations: [],
    upgrades: [],
    note: 'The accompanying paralegal or volunteer attends as a support person only, not to represent, argue, or address proceedings on behalf of the member.',
  },
];

export default function MembershipPolicy() {
  return (
    <div>
      <PageHeader
        eyebrow="Membership Policy"
        title="Membership Contribution Policy & Professional Disclaimer"
        subtitle="Effective Date: 12 November 2025"
      />

      {/* Introduction */}
      <section className="section">
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>Introduction</h2>
          <p>
            Singumkhonto Wabasebenzi (The Spear of Workers) is a registered non-profit,
            paralegal and educational community movement dedicated to empowering workers
            throughout South Africa. We provide guidance, educational support, document
            preparation, and advisory assistance to help employees address workplace
            challenges within the bounds of the law.
          </p>
          <p>
            <strong>We are not a law firm, nor do we act as attorneys or legal representatives.</strong>{' '}
            Our support is educational and paralegal, aimed at helping workers understand
            and handle their own cases confidently.
          </p>
          <p>
            This policy outlines the different levels of support available to workers who
            wish to become part of the organisation and sustain our operations through
            voluntary monthly contributions.
          </p>

          <h2 style={{ marginTop: 40 }}>Purpose of Membership</h2>
          <p>
            Membership contributions are voluntary community support contributions, used
            solely to sustain our educational, paralegal, and case-guidance services. They
            are not legal fees or union dues, and do not entitle any member to legal
            representation. Each membership level provides specific benefits designed to
            ensure that no worker faces unfair labour practices without guidance or moral
            support.
          </p>
        </div>
      </section>

      {/* Membership Tiers */}
      <section className="section" style={{ background: '#f7f5ef' }}>
        <div className="container">
          <span className="eyebrow">Membership Levels & Benefits</span>
          <h2 style={{ marginTop: 12, marginBottom: 32 }}>Choose the level of support that fits your situation</h2>

          <div className="grid grid-3">
            {TIERS.map((tier) => (
              <div className="card" key={tier.name} style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ borderLeft: `4px solid ${tier.color}`, paddingLeft: 14, marginBottom: 16 }}>
                  <h3 style={{ marginBottom: 4 }}>{tier.name}</h3>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: tier.color }}>
                    {tier.price}<span style={{ fontSize: '0.9rem', fontFamily: 'var(--font-body)', color: 'var(--color-grey)' }}> / month</span>
                  </div>
                </div>
                <p style={{ fontStyle: 'italic', marginBottom: 16 }}>{tier.purpose}</p>

                <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 8 }}>Benefits include:</p>
                <ul style={{ marginBottom: 16 }}>
                  {tier.benefits.map((b, i) => (
                    <li key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: '0.9rem' }}>
                      <span style={{ color: tier.color }}>●</span> <span>{b}</span>
                    </li>
                  ))}
                </ul>

                {tier.limitations.length > 0 && (
                  <>
                    <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 8 }}>Limitations:</p>
                    <ul style={{ marginBottom: 16 }}>
                      {tier.limitations.map((l, i) => (
                        <li key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: '0.9rem', color: 'var(--color-grey)' }}>
                          <span>—</span> <span>{l}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {tier.upgrades.length > 0 && (
                  <>
                    <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 8 }}>Optional upgrades:</p>
                    <ul style={{ marginBottom: 16 }}>
                      {tier.upgrades.map((u, i) => (
                        <li key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: '0.9rem' }}>
                          <span style={{ color: tier.color }}>+</span> <span>{u}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {tier.note && (
                  <p style={{ fontSize: '0.82rem', color: 'var(--color-grey)', marginTop: 'auto', paddingTop: 12, borderTop: '1px solid #e7e3d8' }}>
                    Note: {tier.note}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <Link to="/register" className="btn btn-gold">Become a Member</Link>
          </div>
        </div>
      </section>

      {/* Payment and Case Support */}
      <section className="section">
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>Payment and Upgrades</h2>
          <ul>
            <li>All membership contributions are payable monthly in advance via cash, EFT, or secure digital payment.</li>
            <li>Membership upgrades may occur at any time.</li>
            <li>Members of lower categories may access higher-tier services by paying a once-off service contribution: R100 per document drafting/filing (Basic members), or R150 per accompaniment trip (Basic or Standard members).</li>
            <li>All contributions support the sustainability of the organisation's community education and paralegal mission.</li>
          </ul>

          <h2 style={{ marginTop: 32 }}>Case Support and Follow-Up</h2>
          <p>
            All members, regardless of category, are entitled to case follow-up assistance
            until their matter is closed. Follow-ups include checking progress, ensuring
            document submissions are complete, and offering ongoing advisory guidance.
            However, each member remains the legal owner of their own case and must make
            final decisions or representations personally.
          </p>
        </div>
      </section>

      {/* Professional Disclaimer */}
      <section className="dark-section section">
        <div className="container" style={{ maxWidth: 820 }}>
          <span className="eyebrow">Applies to All Membership Levels</span>
          <h2 style={{ marginTop: 12 }}>Professional Disclaimer</h2>

          <ol style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingLeft: 20 }}>
            <li>
              <strong>Non-Legal Status:</strong> The Spear of Workers is not a law firm and
              does not provide legal representation, litigation, or legal opinions. All
              assistance provided is paralegal, educational, and advisory in nature.
            </li>
            <li>
              <strong>Client Responsibility:</strong> Members remain fully responsible for
              their own actions, case submissions, and statements made in any forum (e.g.
              CCMA, Labour Court, workplace hearings).
            </li>
            <li>
              <strong>Scope of Work:</strong> We provide educational guidance on rights and
              procedures, assistance with documentation, preparation, and strategy, and
              supportive accompaniment for confidence and solidarity (not representation).
            </li>
            <li>
              <strong>No Guarantee of Outcome:</strong> We do not guarantee or promise any
              case results or outcomes.
            </li>
            <li>
              <strong>Confidentiality:</strong> All personal information is protected under
              the Protection of Personal Information Act (POPIA). No client or member data
              is shared or sold under any circumstance.
            </li>
            <li>
              <strong>Independence:</strong> The Spear of Workers operates independently and
              is not affiliated with the CCMA, Department of Employment and Labour, or any
              trade union or government body. We may refer members to reputable
              third-party professionals when appropriate.
            </li>
            <li>
              <strong>Use of Contributions:</strong> All funds received through membership
              contributions or document fees are used solely to sustain the organisation's
              educational, administrative, and paralegal support activities.
            </li>
            <li>
              <strong>Ethical Commitment:</strong> We operate under values of solidarity,
              transparency, dignity, and empowerment.
            </li>
          </ol>
        </div>
      </section>

      {/* Termination */}
      <section className="section">
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>Termination or Cancellation of Membership</h2>
          <p>
            Members may cancel their membership at any time by providing written or verbal
            notice. No refunds are issued for past contributions, but membership may be
            reactivated without penalty at any time.
          </p>

          <h2 style={{ marginTop: 32 }}>Contact Information</h2>
          <p>WhatsApp or Call: 071 450 5692 / 079 317 5647 / 072 104 7556</p>
          <p>Email: smartpoint.edustation@gmail.com</p>
          <p>Office: 1363/16 Lindela Wessile Street, Vukuzakhe, Volksrust</p>

          <div className="success-box" style={{ marginTop: 32 }}>
            The Spear of Workers is a community-based paralegal and educational initiative,
            not a law firm or union (unless and until registration is confirmed). All
            activities and contributions fall within the boundaries of educational,
            paralegal, and advocacy support, in compliance with South African law.
          </div>

          <p style={{ fontStyle: 'italic', textAlign: 'center', marginTop: 32, fontSize: '1.05rem' }}>
            "Together we raise the spear for workers' dignity, justice, and empowerment —
            in the workplace and beyond."
          </p>
        </div>
      </section>
    </div>
  );
}
